import React from 'react';

import {Content, DropDown, Trigger} from '../drop-down';
import {List, ListItem, ListValue} from '../list';
import {Button, ButtonAppearance, ButtonSize} from '../inputs/button';
import {ChevronDownIcon} from '../svg-icon';
import {cls} from '../../utils';

import css from './split-button.module.css';

export type SplitButtonItem = {
	value: ListValue;
	label: React.ReactNode;
	disabled?: boolean;
};

export type SplitButtonProps = {
	/** подпись основной кнопки */
	children: React.ReactNode;
	/** основное действие: выполняется с выбранным вариантом */
	onClick(): void;
	/** варианты в списке по стрелке */
	items: SplitButtonItem[];
	/** выбранный вариант: он подсвечен в списке и от него зависит основное действие */
	value?: ListValue;
	/** выбор в списке меняет вариант, но не выполняет действие */
	onChange?(value: ListValue): void;
	/** выбор в списке сразу выполняет действие с этим вариантом, а выбранный вариант не меняется */
	onSelect?(value: ListValue): void;
	/** над списком: заголовок или предупреждение */
	menuHeader?: React.ReactNode;
	/** подпись стрелки для экранных читалок */
	menuLabel: string;
	/** всплывающая подсказка основной кнопки */
	title?: string;
	disabled?: boolean;
	appearance?: ButtonAppearance;
	size?: ButtonSize;
	/** на всю ширину: основная кнопка растягивается */
	block?: boolean;
	className?: string;
	/** класс основной кнопки */
	buttonClassName?: string;
};

export const SplitButton: React.FC<SplitButtonProps> = ({
	children,
	onClick,
	items,
	value,
	onChange,
	onSelect,
	menuHeader,
	menuLabel,
	title,
	disabled,
	appearance = 'secondary',
	size,
	block,
	className,
	buttonClassName,
}) => {
	const isMenuDisabled = disabled || items.length < (onSelect ? 1 : 2);
	return (
		<span className={cls(css.splitButton, block && css.block, className)}>
			<Button
				className={cls(css.main, buttonClassName)}
				appearance={appearance}
				size={size}
				disabled={disabled}
				title={title}
				onClick={onClick}
			>
				{children}
			</Button>
			<DropDown
				className={css.menu}
				selectMode="single"
				value={value === undefined ? [] : [value]}
				onChange={([selected]) => {
					const picked = selected ?? value;
					if (picked === undefined) return;
					if (onSelect) onSelect(picked);
					else onChange?.(picked);
				}}
				disabled={isMenuDisabled}
			>
				<Trigger>
					<Button
						className={css.toggle}
						appearance={appearance}
						size={size}
						disabled={isMenuDisabled}
						icon={<ChevronDownIcon width={12} height={12} />}
						aria-label={menuLabel}
					/>
				</Trigger>
				<Content align="end">
					{menuHeader && <div className={css.menuHeader}>{menuHeader}</div>}
					<List selectAppearance="selected" aria-label={menuLabel}>
						{items.map((item) => (
							<ListItem key={String(item.value)} value={item.value} disabled={item.disabled}>
								{item.label}
							</ListItem>
						))}
					</List>
				</Content>
			</DropDown>
		</span>
	);
};
